import React, { useEffect, useContext } from 'react';
import { isEmpty } from 'lodash';
import styled, { css } from 'styled-components';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Link, useLocation, Routes, Route } from 'react-router';
import { Container, Card, Flex, Box, Text } from '../../design-system';
import { useData } from '../../helpers/data-loader';
import config from '../../config';
import Agency from './Agency';

const stripePromise = loadStripe(config.STRIPE_KEY);
import Billing from './Billing';
import Loadable from '../../components/Loadable';
import Profile from './Profile';
import Subscription from './Subscription';
import UserMGMT from './UserMGMT';
import PermissionRoute from '../../routes/route';
import { ERR_MSG, PERMISSIONS } from '../../globalConstants';
import { Session } from '../../contexts/sessionContext';

const UL = styled.ul`
  list-style-type: none;
  margin: 10px;
  padding: 0;
  display: flex;
  flex-direction: column;

  li {
    padding: 15px;
  }
`;

const StyledLink = styled(Link)<{ selected?: boolean }>`
  ${({ selected, theme }) => css`
    text-decoration: none;
    color: ${selected ? theme.colors.primary : 'inherit'};
    font-weight: ${selected && 700};
    border-bottom: ${selected && `2px solid ${theme.colors.primary}`};
  `}
`;

const Account = () => {
  const { pathname } = useLocation();
  const {
    state: { user },
    dispatch,
  } = useContext(Session);
  const [agency, fetchAgency, agencyLoading] = useData('/agency', {
    errorMsg: ERR_MSG,
  });
  const [billing, fetchBilling, billingLoading] = useData('/billing', {
    errorMsg: 'Unable to access user billing',
  });
  useEffect(() => {
    fetchAgency();
    if (user.permissions.includes(PERMISSIONS.UPDATE_BILLING)) fetchBilling();
  }, []);

  const views = [
    {
      permission: PERMISSIONS.READ_PROFILE,
      route: '/account',
      label: 'Profile',
    },
    {
      permission: PERMISSIONS.CREATE_USERS,
      route: '/account/user-mgmt',
      label: 'User Management',
    },
    {
      permission: PERMISSIONS.UPDATE_AGENCY,
      route: '/account/agency',
      label: 'Agency Information',
    },
    {
      permission: PERMISSIONS.UPDATE_BILLING,
      route: '/account/billing',
      label: 'Billing Information',
      noDisplay: isEmpty(billing) && true,
    },
    {
      permission: PERMISSIONS.UPDATE_SUBSCRIPTIONS,
      route: '/account/subscription',
      label: 'Subscription Information',
    },
  ];

  return (
    <Loadable loading={billingLoading || agencyLoading}>
      <Container fluid mt={10} mb={30}>
        <Box pb={2}>
          <Text fontSize={22} fontWeight={600}>
            Account Management
          </Text>
        </Box>
        <Flex>
          <Box mt={2} width="18%" maxWidth={300}>
            <Card minHeight="48rem">
              <UL>
                {views.map(
                  ({ permission, route, label, noDisplay }) =>
                    user.permissions.includes(permission) &&
                    !noDisplay && (
                      <li key={route}>
                        <StyledLink selected={route === pathname} to={route}>
                          {label}
                        </StyledLink>
                      </li>
                    ),
                )}
              </UL>
            </Card>
          </Box>
          <Box ml={3} mt={2} width="75%">
            <Routes>
              <Route
                index
                element={
                  <PermissionRoute
                    noAccess={null}
                    render={() => <Profile user={user} dispatch={dispatch} />}
                  />
                }
              />
              <Route
                path="user-mgmt"
                element={
                  <PermissionRoute
                    noAccess={null}
                    allowedPermissions={[PERMISSIONS.CREATE_INVITES]}
                    render={() => <UserMGMT user={user} dispatch={dispatch} />}
                  />
                }
              />
              <Route
                path="agency"
                element={
                  <PermissionRoute
                    noAccess={null}
                    allowedPermissions={[PERMISSIONS.UPDATE_AGENCY]}
                    render={() => (
                      <Agency agency={agency} refetch={fetchAgency} />
                    )}
                  />
                }
              />
              {!isEmpty(billing) && (
                <Route
                  path="billing"
                  element={
                    <PermissionRoute
                      noAccess={null}
                      allowedPermissions={[PERMISSIONS.UPDATE_BILLING]}
                      render={() => (
                        <Elements stripe={stripePromise}>
                          <Billing billing={billing} refetch={fetchBilling} />
                        </Elements>
                      )}
                    />
                  }
                />
              )}
              <Route
                path="subscription"
                element={
                  <PermissionRoute
                    noAccess={null}
                    allowedPermissions={[PERMISSIONS.UPDATE_SUBSCRIPTIONS]}
                    render={() => <Subscription refetch={fetchAgency} />}
                  />
                }
              />
            </Routes>
          </Box>
        </Flex>
      </Container>
    </Loadable>
  );
};

export default Account;
